import { useRef, useState } from 'react';
import { useTranslations } from 'use-intl';
import { Card, PageHeader, Stack, useDelayedLoading } from '@leanin/ui';
import { SaveBar } from '../../form/SaveBar';
import { useNetwork } from '../../../lib/queries/groups';
import { useUpdateNetwork } from '../../../lib/mutations/groups';
import { useErrorMessage } from '../../../lib/error-message';
import {
  AdminImagePanel,
  type AdminImagePanelHandle,
} from '../../group/admin/AdminImagePanel';
import { AdminFormPageSkeleton } from '../../group/admin/AdminFormPageSkeleton';
import {
  NetworkProfileFields,
  socialDraftToPayload,
  type NetworkProfileDraft,
} from './NetworkProfileFields';

type Props = {
  readonly networkId: string;
};

export function NetworkOverviewSection({ networkId }: Props) {
  const detailQuery = useNetwork(networkId);
  const network = detailQuery.data;

  const showSkeleton = useDelayedLoading(network === undefined);
  if (showSkeleton) {
    return <AdminFormPageSkeleton withAction cards={1} rowsPerCard={5} />;
  }
  if (network === undefined) return null;

  return <NetworkOverviewForm network={network} />;
}

type FormProps = {
  readonly network: NonNullable<ReturnType<typeof useNetwork>['data']>;
};

function draftFromNetwork(
  network: FormProps['network'],
): NetworkProfileDraft {
  const socials = network.socialLinks ?? {};
  return {
    name: network.name,
    tagline: network.tagline ?? '',
    region: network.region,
    mission: network.mission ?? '',
    description: network.description ?? '',
    welcomeMessage: network.welcomeMessage ?? '',
    joinInstructions: network.joinInstructions ?? '',
    tags: network.tags ?? [],
    social: {
      website: socials.website ?? '',
      linkedin: socials.linkedin ?? '',
      instagram: socials.instagram ?? '',
      facebook: socials.facebook ?? '',
      twitter: socials.twitter ?? '',
      email: socials.email ?? '',
    },
  };
}

function NetworkOverviewForm({ network }: FormProps) {
  const tn = useTranslations('Networks');
  const describeError = useErrorMessage();
  const updateMutation = useUpdateNetwork();
  // `initial` is the saved baseline the dirty-check compares against. It must
  // be re-synced after a successful save (see handleSave onSuccess) — without
  // that the SaveBar never clears even though the write landed, and a reload
  // is the only way to reset it.
  const [initial, setInitial] = useState(() => draftFromNetwork(network));
  const [draft, setDraft] = useState<NetworkProfileDraft>(initial);
  const [error, setError] = useState<string | null>(null);

  const networkId = network.id;
  const imagesRef = useRef<AdminImagePanelHandle | null>(null);
  const [imagesPending, setImagesPending] = useState(false);
  const [committing, setCommitting] = useState(false);
  const isPending = updateMutation.isPending || committing;

  const socialsDirty =
    JSON.stringify(draft.social) !== JSON.stringify(initial.social);
  const textDirty = JSON.stringify(draft) !== JSON.stringify(initial);
  const dirty = textDirty || imagesPending;

  const patch = (p: Partial<NetworkProfileDraft>) =>
    setDraft((d) => ({ ...d, ...p }));

  const handleSave = async () => {
    if (!dirty) return;
    setError(null);
    if (draft.name.trim().length === 0) {
      setError(tn('overviewAdmin.errors.nameRequired'));
      return;
    }
    if (imagesPending) {
      setCommitting(true);
      try {
        await imagesRef.current?.commit();
      } catch (err) {
        setError(describeError(err, tn('overviewAdmin.errors.imageUploadFailed')));
        setCommitting(false);
        return;
      }
      setCommitting(false);
    }
    if (!textDirty) return;

    // Normalize once (trim) so the persisted values and the new dirty-check
    // baseline are identical — otherwise trailing whitespace would leave the
    // form looking dirty after a successful save.
    const saved: NetworkProfileDraft = {
      ...draft,
      name: draft.name.trim(),
      tagline: draft.tagline.trim(),
      mission: draft.mission.trim(),
      description: draft.description.trim(),
      welcomeMessage: draft.welcomeMessage.trim(),
      joinInstructions: draft.joinInstructions.trim(),
    };

    updateMutation.mutate(
      {
        networkId,
        name: saved.name,
        description: saved.description,
        tagline: saved.tagline || null,
        mission: saved.mission || null,
        welcomeMessage: saved.welcomeMessage || null,
        joinInstructions: saved.joinInstructions || null,
        tags: saved.tags,
        region: saved.region,
        // SocialLinksSchema is strict — only send the trimmed, blank-stripped
        // object when the social block actually changed.
        ...(socialsDirty ? { socialLinks: socialDraftToPayload(saved.social) } : {}),
      },
      {
        onSuccess: () => {
          // Re-baseline to the just-saved values so `dirty` flips false: the
          // SaveBar clears (and shows its "Saved." flash) instead of lingering.
          setInitial(saved);
          setDraft(saved);
          setError(null);
        },
        onError: (err) =>
          setError(describeError(err, tn('overviewAdmin.errors.updateFailed'))),
      },
    );
  };

  return (
    <Stack gap="lg">
      <PageHeader title={tn('overviewAdmin.title')} />

      <Card pad="flush">
        <AdminImagePanel
          ref={imagesRef}
          groupKind="network"
          groupId={networkId}
          groupName={network.name}
          currentCoverUrl={network.imageUrl ?? null}
          currentCoverOriginalUrl={network.imageOriginalUrl}
          currentCoverCrop={network.imageCrop}
          currentLogoUrl={network.logoUrl ?? null}
          currentLogoOriginalUrl={network.logoOriginalUrl}
          currentLogoCrop={network.logoCrop}
          onPendingChange={setImagesPending}
        />
        <NetworkProfileFields value={draft} onChange={patch} />
      </Card>

      <SaveBar
        dirty={dirty}
        saving={isPending}
        error={error}
        onSave={handleSave}
      />
    </Stack>
  );
}
